import type { Candidate } from '@ni/shared';

/**
 * **Cards the merger thinks are one person** (2026-10-14), gathered for the pick
 * list and the held list. A card's `likely_same_as` names other cards on the
 * same list; following those links in either direction, through any number of
 * hops, gives the groups the page draws together. Only links between cards on
 * this list count — the server's rule too (`same-person-links.ts`), which leaves
 * the rest of a group open when the office confirms a single card.
 */

export type ListItem =
  | { kind: 'card'; candidate: Candidate }
  | {
      kind: 'group';
      members: Candidate[];
      /** What the linked cards have in common, one line per fact, each said once. */
      shared: string[];
      /** What one says that disagrees with another, each said once. */
      conflicts: string[];
    };

function neighbors(candidates: readonly Candidate[]): Map<string, Set<string>> {
  const refs = new Set(candidates.map((c) => c.candidate_ref));
  const edges = new Map<string, Set<string>>(candidates.map((c) => [c.candidate_ref, new Set()]));
  for (const candidate of candidates) {
    for (const link of candidate.likely_same_as) {
      if (link.candidate_ref === candidate.candidate_ref || !refs.has(link.candidate_ref)) continue;
      edges.get(candidate.candidate_ref)?.add(link.candidate_ref);
      edges.get(link.candidate_ref)?.add(candidate.candidate_ref);
    }
  }
  return edges;
}

/** Every card reachable from `chosen` through links, the chosen ones excluded. */
function reach(edges: ReadonlyMap<string, ReadonlySet<string>>, chosen: readonly string[]) {
  const start = new Set(chosen);
  const reached = new Set<string>();
  const queue = [...chosen];
  while (queue.length > 0) {
    const ref = queue.shift() as string;
    for (const next of edges.get(ref) ?? []) {
      if (start.has(next) || reached.has(next)) continue;
      reached.add(next);
      queue.push(next);
    }
  }
  return reached;
}

/**
 * The list in **server order**, with each group drawn where its first card
 * stands. A card nothing links is a card; the order of everything else is never
 * changed (the pick list is never re-sorted, ui-design §4.5).
 */
export function groupLinked(candidates: readonly Candidate[]): ListItem[] {
  const edges = neighbors(candidates);
  const byRef = new Map(candidates.map((c) => [c.candidate_ref, c]));
  const placed = new Set<string>();
  const items: ListItem[] = [];
  for (const candidate of candidates) {
    const ref = candidate.candidate_ref;
    if (placed.has(ref)) continue;
    const group = new Set([ref, ...reach(edges, [ref])]);
    if (group.size < 2) {
      placed.add(ref);
      items.push({ kind: 'card', candidate });
      continue;
    }
    const members = candidates.filter((c) => group.has(c.candidate_ref));
    members.forEach((c) => placed.add(c.candidate_ref));
    items.push({ kind: 'group', members, ...whatTheyShare(members, byRef) });
  }
  return items;
}

/** Each pair's link once — the merger writes it on both cards, sides swapped. */
function whatTheyShare(
  members: readonly Candidate[],
  byRef: ReadonlyMap<string, Candidate>,
): { shared: string[]; conflicts: string[] } {
  const seenPairs = new Set<string>();
  const shared: string[] = [];
  const conflicts: string[] = [];
  for (const member of members) {
    for (const link of member.likely_same_as) {
      if (!byRef.has(link.candidate_ref)) continue;
      const pair = [member.candidate_ref, link.candidate_ref].sort().join('\u0000');
      if (seenPairs.has(pair)) continue;
      seenPairs.add(pair);
      shared.push(...link.shared);
      conflicts.push(...link.conflicts);
    }
  }
  return { shared: [...new Set(shared)], conflicts: [...new Set(conflicts)] };
}
